// @ts-strict-ignore
import React from 'react';
import { Icon, IconName } from '@usealloy/component-library';
import type {
  Action,
  AttributeComponent,
  AttributeComponentDefinition,
  DataRoute,
  ServicesByName,
  Threshold,
  ThresholdLeft,
  ThresholdRight,
  Attribute,
} from 'flow/store/engine/types';
import { CaretPositionSuffixTypes } from 'flow/editor/components/NodeEditorDrawer/types';
import { DOLLAR_THRESHOLD_MLA_FORMULA_PLACEHOLDERS } from 'flow/editor/components/thresholds/constants';
import { capitalizeString } from 'flow/editor/components/helpers';
import type { AttributeDescriptionTokens, PresentationalToken, RulesWithMLA } from 'flow/editor/components/mla/types';
import { MLA_FILTER_PLACEHOLDERS_NO_LABEL } from 'flow/editor/components/mla/constants';
import type { MLATokenType } from './constants';
import { MLAServices, MLAType } from './constants';
import {
  customizeAHBalanceMLAEditableFilters,
  customizeAHBalanceMLAFootnote,
  customizeAHStatusMLAFootnote,
  generateTokensWithLabelCasing,
} from './accountHistory';
import { customizeIEVMLAFootnote } from './iev';
import { getAllThresholdFromRules } from '../thresholds';
import { isActionOfType } from '../actions';
import { ActionType } from 'flow/store/engine/constants';
import { extractDataRouteIds, isDataRouteAnMLA } from '../dataRoutes';
import { customizeEHDistinctPIIMLAFootnote } from './entityHistory';
import type { CustomList } from '../actions/services';
import {
  CUSTOM_LIST_SCORE_PLACEHOLDER,
  updateCustomListAttribute,
  isCustomListAttribute,
} from '../actions/services';

export type MLADataTokens<T extends MLATokenType> = T extends MLATokenType.FOOTNOTE
  ? PresentationalToken[]
  : AttributeDescriptionTokens;

function getCaretSuffixForMLAComponent(dataType: string) {
  switch (dataType) {
    case 'select_one':
      return CaretPositionSuffixTypes.MLA_SELECT_ONE;
    case 'multi_select':
      return CaretPositionSuffixTypes.MLA_MULTI_SELECT;
    case 'csv':
      return CaretPositionSuffixTypes.MLA_CSV;
    case 'integer':
      return CaretPositionSuffixTypes.MLA_INTEGER;
    case 'boolean':
      return CaretPositionSuffixTypes.MLA_BOOLEAN;
    default:
      return '';
  }
}

export function getSortedMLAComponentsByPrecedence({
  mlaData,
}: {
  mlaData: AttributeComponent[];
}) {
  return [...mlaData].sort((
    { attributeComponent: attrComponentA },
    { attributeComponent: attrComponentB },
  ) => attrComponentA.precedence - attrComponentB.precedence);
}

export function generateMLAComponentCaret({
  filterComponent,
}: {
  filterComponent: AttributeComponent;
}) {
  const { dataType, isOptional, precedence } = filterComponent.attributeComponent;
  const typeSuffix = getCaretSuffixForMLAComponent(dataType);
  const requiredSuffix = !isOptional ? CaretPositionSuffixTypes.MLA_REQUIRED : '';
  const isValueSet = filterComponent.value !== undefined ? CaretPositionSuffixTypes.MLA_VALUE_SET : ''; // value can be 0
  return `${precedence}${typeSuffix}${requiredSuffix}${isValueSet}`;
}

export function flattenMLAComponentIndices({
  mlaData,
}: {
  mlaData: AttributeComponent[];
}) {
  const sortedMlaComponents = getSortedMLAComponentsByPrecedence({ mlaData });

  return sortedMlaComponents.map(
    component => generateMLAComponentCaret({ filterComponent: component }));
}

export function getAlphanumericCharacters(value: string) {
  // this regex is to only grab alphanumeric characters, hyphen, and underscore; also we use comma as delimiter, no space allowed.
  // the first `[^\s!%^()$=+./?'";:\][{},]` part is where it says "not look for those" - including \s: whitespace
  // the second `[a-zA-Z0-9@#&\-_*]` and `+` after that is to look for those items specifically, and `*` means 0 or more token of that.
  return value.match(/([^\s!%^()$=+./?'";:\][{},][a-zA-Z0-9@#&\-_*]*)/g);
}

export function getMLADescriptionTokens(description: string) {
  /** this grabs "token" from the description string.
  * mla attribute description string looks like this: The {formula} of whatever blah is higher than {min_amount} but lower than {max_amount}
  * this regex grabs a word token that doesn't have { or } around it ("The", "of", "whatever", "blah", ...)
  * ^ This part in the regex is `(\w+\d*)[^{}\s])`
  *
  * and also grabs ',' comma as its own token because we need to render that for visual.
  * ^ This part in the regex is `|,|`
  *
  * and also grabs a { }-wrapped token ("{formula}", "{min_amount}", ...)
  * ^ This part in the regex is `{\w+}`
  *
  * and also grabs a ( )-wrapped token ("(avg)", "(exact match)", ...)
  * ^ This part in the regex is `(\([^)]+\))`
  */
  return description.match(/((\w+\d*)[^{}\s])|,|{\w+}|(\([^)]+\))/g);
}

export function getDollarCurrencyValue(value: string | number) {
  return (Number(value) / 100).toFixed(2);
}

export function getMLAFilterComponentLabel({
  componentData,
}: {
  componentData: AttributeComponentDefinition;
}) {
  // these are hard coded labels because the backend data lacks.
  switch (componentData.placeholder) {
    case '{time_frame}':
      return 'Time Frame';
    case '{interval}':
      return 'Interval';
  }

  return componentData.label;
}

export function getMLAComponentOptions({
  component,
  isCurrencyValueRequired,
  configuredValue,
}: {
  component: AttributeComponentDefinition;
  isCurrencyValueRequired: boolean;
  configuredValue: string | number | (string | number)[];
}) {
  const optionalComponent = component.isOptional;
  const deleteOption = {
    value: 'delete_filter',
    label: 'Delete this filter',
    icon: <Icon icon={IconName.Trash} color="var(--cl-red-500)" size="small" />,
  };

  if (Array.isArray(component.possibleValues)) {
    const allPossibleValues = component.possibleValues.map(value => {
      let customLabel = String(value[1]);
      if (component.placeholder === '{status}') {
        if (['all', 'any'].includes(value[0])) {
          customLabel = 'either Accepted or Declined (All)';
        } else {
          customLabel = String(value[1]);
        }
      }

      if (['{is_international}', '{usertriggered}'].includes(component.placeholder)) {
        if (['all', 'any'].includes(value[0])) {
          customLabel = `${value[1]} ${component.label.toLowerCase()}`;
        } else if (component.placeholder === '{usertriggered}') {
          customLabel = value[0] === 'user_initiated'
            ? 'Initiated by user'
            : 'Not initiated by user';
        } else {
          customLabel = String(value[1]);
        }
      }
      return {
        value: String(value[0]),
        label: customLabel,
      };
    });
    // possibleValues come from db and if they are an array, it'll be pair of value and displayable "label"
    // e.g. ["all", "All"] or ["distinct_avg", "Avg"]
    return optionalComponent
      ? [...allPossibleValues, deleteOption]
      : allPossibleValues;
  }

  if (component.dataType === 'boolean') {
    if (component.placeholder === '{only_counterparty}') {
      return [
        {
          value: 'true',
          label: 'to the counterparty being evaluated',
        },
        deleteOption,
      ];
    }

    if (component.placeholder === '{only_source_account}') {
      return [
        {
          value: 'true',
          label: 'with this source account',
        },
        deleteOption,
      ];
    }

    return [
      {
        value: 'true',
        label: 'True',
      },
      deleteOption,
    ];
  }

  if (configuredValue) {
    const formattedValue = isCurrencyValueRequired
      ? getDollarCurrencyValue(configuredValue as string)
      : configuredValue;
    const newOption = {
      value: String(configuredValue),
      label: String(formattedValue),
    };

    return optionalComponent
      ? [newOption, deleteOption]
      : [newOption];
  }

  return [];
}

export const checkCurrencyValueFormatRequired = ({
  component,
  attributeName,
}: {
  component: AttributeComponentDefinition;
  attributeName: string;
}) => DOLLAR_THRESHOLD_MLA_FORMULA_PLACEHOLDERS.find(
  dollarPlaceholder => component.placeholder.includes(dollarPlaceholder),
) && ['th_multilevel', 'th_nonmonetary_multilevel'].includes(attributeName);

export const getPresentationalValue = ({
  configuredValue,
  component,
  attributeName,
  labelRequirements,
  shouldReturnValueOnly,
}: {
  configuredValue: string | number | (string | number)[];
  component: AttributeComponentDefinition;
  attributeName: string;
  labelRequirements: boolean | string;
  shouldReturnValueOnly?: boolean;
}) => {
  if (!configuredValue) return '';
  const isCurrencyValueRequired = checkCurrencyValueFormatRequired({
    component,
    attributeName,
  });
  const presentationalOptions = getMLAComponentOptions({
    component,
    isCurrencyValueRequired,
    configuredValue,
  });
  let componentLabel = component.label;
  if (![
    '{pii_element}',
  ].includes(component.placeholder)) {
    componentLabel = componentLabel?.toLowerCase();
  }

  if (component.dataType === 'multi_select') {
    let prepositions = '';
    if (component.placeholder === '{interaction_point}') {
      prepositions = 'in ';
    } else if (component.placeholder === '{process_method}') {
      prepositions = 'on ';
    }

    const isStatusFilter = component.label === 'Status';
    const isOutcomeFilter = component.placeholder === '{outcome}';
    const isTagFilter = component.placeholder === '{tag}';
    const matchedOptions = Array.isArray(configuredValue)
      ? presentationalOptions.filter(
        ({ value }) => (configuredValue as string[]).find(
          attributeValue => String(attributeValue).toLowerCase() === value.toLowerCase()))
      : [];

    if (typeof configuredValue === 'string' || matchedOptions.length === 1) {
      if (typeof configuredValue === 'string' && ['all', 'any'].includes(configuredValue)) {
        return `${prepositions}${configuredValue} ${componentLabel},`;
      }
      if (['all', 'any'].includes(matchedOptions[0]?.value)) {
        if (shouldReturnValueOnly) {
          return matchedOptions[0].value;
        }
        if (isStatusFilter) {
          return `with the ${componentLabel} of ${matchedOptions[0].value},`;
        }
        return `${prepositions}${matchedOptions[0].value} ${componentLabel},`;
      }
      if (shouldReturnValueOnly) {
        return configuredValue;
      }
      if (isOutcomeFilter || isTagFilter) {
        return `${componentLabel} of '${matchedOptions[0].label}'`;
      }
      return `${configuredValue} ${componentLabel},`;
    }

    if (componentLabel && !componentLabel.endsWith('s')) {
      if (componentLabel.endsWith('y')) {
        componentLabel = `${componentLabel.replace('y', 'ie')}s`;
      } else {
        componentLabel = `${componentLabel}s`;
      }
    }

    if (!matchedOptions?.[0]) return '';
    const joinedOptions = matchedOptions.map(option => option.label).join('/');
    if (component.placeholder === '{interaction_point}') {
      return `for ${joinedOptions} ${componentLabel},`;
    }
    if (component.placeholder === '{process_method}') {
      return `processed via ${joinedOptions},`;
    }
    if (component.placeholder === '{category}') {
      return `of ${joinedOptions} ${componentLabel},`;
    }

    if (isStatusFilter) {
      return `the ${componentLabel} of ${joinedOptions},`;
    }
    if (isOutcomeFilter || isTagFilter) {
      return `${componentLabel} of '${joinedOptions}'`;
    }
    if (shouldReturnValueOnly) {
      return joinedOptions;
    }
    return `${componentLabel} of ${joinedOptions},`;
  }

  if (component.dataType === 'csv') {
    if (!Array.isArray(configuredValue)) {
      // usually if this is not an array, it's a malformed config.
      return '';
    }

    const incomingValue = configuredValue.join('/');
    return `with the following ${componentLabel}: ${incomingValue},`;
  }

  if (['select_one', 'integer', 'boolean', 'decimal'].includes(component.dataType)) {
    return presentationalOptions.filter(({ value }) => String(configuredValue) === value)
      .map(option => {
        const value = isCurrencyValueRequired
          ? getDollarCurrencyValue(option.value)
          : option.value;

        let formattedLabel = option.label.toLowerCase();
        if (['min', 'minimum'].includes(option.value)) {
          formattedLabel = 'minimum';
        }

        if (['max', 'maxmimum'].includes(option.value)) {
          formattedLabel = 'maximum';
        }

        if (['avg', 'average', 'distinct_average'].includes(option.value)) {
          formattedLabel = 'average';
        }
        const currencyReadyFormat = isCurrencyValueRequired
          ? `$${value}`
          : formattedLabel;

        if (shouldReturnValueOnly) {
          return currencyReadyFormat;
        }

        if (labelRequirements === 'all') {
          return `all ${component.label},`;
        }

        if (labelRequirements === 'any') {
          return `any ${component.label},`;
        }

        if (component.placeholder === '{pii_status}' || component.placeholder === '{entity_status}') {
          return `, ${component.label.toLowerCase()} of '${option.value}'`;
        }

        if (labelRequirements === 'true') {
          if (component.placeholder === '{only_counterparty}') {
            return 'to the counterparty being evaluated,';
          }

          if (component.placeholder === '{only_source_account}') {
            return 'with this source account,';
          }
          return `that are ${component.label},`;
        }

        if (labelRequirements === 'false') {
          return `that are not ${component.label},`;
        }

        if (['{flow}', '{status}'].includes(component.placeholder)) {
          return `${component.label.toLowerCase()} is ${capitalizeString(currencyReadyFormat)},`;
        }

        if (['{date_range_start}', '{date_range_end}'].includes(component.placeholder)) {
          if (!attributeName.includes('monetary')) {
            return currencyReadyFormat;
          }
          return `${component.placeholder === '{date_range_start}' ? '' : 'to'} ${currencyReadyFormat}`;
        }

        if (['{amount}', '{max_amount}', '{min_count}', '{max_count}'].includes(component.placeholder)) {
          if (component.placeholder === '{amount}') {
            return `of an amount more than ${currencyReadyFormat},`;
          }

          if (component.placeholder === '{max_amount}') {
            return `of an amount less than ${currencyReadyFormat},`;
          }
          return `${component.label} of ${currencyReadyFormat},`;
        }

        if (component.placeholder === '{usertriggered}') {
          return `${capitalizeString(currencyReadyFormat)},`;
        }

        return currencyReadyFormat;
      });
  }

  return '';
};

export const getLabelRequirementsForFootnoteSentence = ({
  token,
  attributeName,
}: {
  token: PresentationalToken;
  attributeName: string;
}) => {
  if (token.component) {
    if (['th_nonmonetary_multilevel', 'th_counterparty_multilevel', 'th_multilevel'].includes(attributeName)) {
      const isArrayValue = Array.isArray(token.value) && typeof token.value[0] === 'string';
      const valueToCheck = isArrayValue ? token.value[0] : token.value;
      if (
        (typeof token.value === 'string' || isArrayValue)
        && ['all', 'any', 'true', 'false'].includes(valueToCheck)
      ) {
        return valueToCheck;
      }

      if (
        (
          MLA_FILTER_PLACEHOLDERS_NO_LABEL.includes(token.placeholder)
          || ['multi_select', 'csv'].includes(token.component.dataType)
        ) && token.value
      ) return false;
    }
  }

  return true;
};

export function generateTokenObject(component: AttributeComponent) {
  return {
    label: getMLAFilterComponentLabel({
      componentData: component.attributeComponent,
    }),
    isOptional: component.attributeComponent.isOptional,
    component: component.attributeComponent,
    placeholder: component.attributeComponent.placeholder,
    value: component.value,
  };
}

export function checkIfServiceHasMLAAttributes(serviceName: string) {
  return MLAServices.includes(serviceName);
}

export function generateCustomAttributeTokens({
  attribute,
  components,
  descTokens,
}: {
  attribute: Attribute;
  components: AttributeComponent[];
  descTokens: AttributeComponent[] | RegExpMatchArray;
}) {
  let tokensWithIndex: AttributeDescriptionTokens = [];
  if (['th_multilevel', 'th_nonmonetary_multilevel'].includes(attribute.attribute_name)) {
    return customizeTransactionHistoryMLAEditableFilters({ attribute, descTokens });
  }

  tokensWithIndex = descTokens.map(token => {
    if (token.includes('{')) {
      const componentData = components.find(
        component => component.attributeComponent.placeholder === token,
      );

      if (
        componentData.attributeComponent.dataType === 'select_one'
        && Array.isArray(componentData.attributeComponent.possibleValues)
        && componentData.attributeComponent.possibleValues?.length === 1
      ) {
        return {
          string: componentData.attributeComponent.possibleValues[0][1], // 2nd one is the display label
          isOptional: false,
          caretIndex: undefined,
        };
      }
      const tokenObject = {
        string: token,
        isOptional: componentData?.attributeComponent.isOptional,
        caretIndex: componentData?.attributeComponent.precedence,
      };
      return tokenObject;
    }

    return {
      string: token,
      isOptional: false,
      caretIndex: undefined,
    };
  });

  switch (attribute.attribute_name) {
    case 'ah_balance_multilevel':
      return customizeAHBalanceMLAEditableFilters({ tokensWithIndex });
    default:
      break;
  }

  return tokensWithIndex;
}

export function applyCustomCasingOnLabelTokens({
  token,
}: {
  token: string;
}) {
  if ([
    'journey',
    'application',
    'prove',
    'equifax',
    'experian',
    'transunion',
  ].includes(token)) {
    return capitalizeString(token);
  }

  return token;
}

export function generateCustomFootnoteTokens({
  attribute,
  components,
  descTokens,
}: {
  attribute: Attribute;
  components: AttributeComponent[];
  descTokens: AttributeComponent[] | RegExpMatchArray;
}) {
  let presentationalTokens: PresentationalToken[] = [];
  if (['th_multilevel', 'th_nonmonetary_multilevel'].includes(attribute.attribute_name)) {
    return customizeTransactionHistoryMLAFootnote({ attribute, descTokens });
  }

  const processedTokens = descTokens.map((token, tokenIdx) => {
    if (token.includes('{')) {
      const componentData = components.find(
        component => component.attributeComponent.placeholder === token,
      );
      return generateTokenObject(componentData);
    }

    const tokenWithCustomCasing = applyCustomCasingOnLabelTokens({ token });

    return {
      label: tokenWithCustomCasing,
      placeholder: `${token}-${tokenIdx}`,
      component: null,
      isOptional: false,
      value: null,
    };
  });

  switch (attribute.attribute_name) {
    case 'alh_number_journey_alerts':
      presentationalTokens = customizeALHNumberJourneysOrALHNumberInvestigationsMLAFootnote({
        processedTokens,
      });
      break;
    case 'alh_number_investigations':
      presentationalTokens = customizeALHNumberJourneysOrALHNumberInvestigationsMLAFootnote({
        processedTokens,
      });
      break;
    case 'th_counterparty_multilevel':
      presentationalTokens = customizeTHCounterpartyMLAFootnote({ processedTokens });
      break;
    case 'ah_balance_multilevel':
      presentationalTokens = customizeAHBalanceMLAFootnote({ processedTokens });
      break;
    case 'ah_status_multilevel':
      presentationalTokens = customizeAHStatusMLAFootnote({ processedTokens });
      break;
    case 'ah_acccount_changes_multilevel':
      presentationalTokens = generateTokensWithLabelCasing({ processedTokens });
      break;
    case 'ph_decision_multilevel':
      presentationalTokens = customizeIEVMLAFootnote({ processedTokens });
      break;
    case 'eh_pii_change_multilevel_v2': {
      const piiStatusComponent = components.find(
        component => component.attributeComponent.placeholder === '{pii_status}',
      );
      processedTokens.push({
        label: 'PII status',
        placeholder: '{pii_status}',
        component: piiStatusComponent.attributeComponent,
        isOptional: piiStatusComponent.attributeComponent.isOptional,
        value: piiStatusComponent.value,
      });
      presentationalTokens = customizeEHDistinctPIIMLAFootnote({ processedTokens });
    }
      break;
    default:
      presentationalTokens = processedTokens;
      break;
  }

  return presentationalTokens;
}


export function getAllMLAsWithFootnoteStringTokens({
  actions,
  rulesWithMLA,
  services,
  customLists,
}: {
  actions: Record<string, Action>;
  rulesWithMLA: RulesWithMLA;
  services: ServicesByName;
  customLists: CustomList[];
}) {
  return rulesWithMLA.map(rule => {
    const ruleActionId = isRuleWithMLAAThreshold(rule)
      ? (rule.data as ThresholdLeft | ThresholdRight).actionId
      : (rule.data as DataRoute).sourceId;

    if (!isActionOfType(actions[ruleActionId], ActionType.SERVICE)) return null;
    const serviceName = actions[ruleActionId]?.service.internalName;
    const attribute = isRuleWithMLAAThreshold(rule)
      ? services[serviceName].attributes[
        (rule.data as ThresholdLeft | ThresholdRight).attributeId]
      : services[serviceName].attributes[(rule.data as DataRoute).attribute.id];

    let components = isRuleWithMLAAThreshold(rule)
      ? (rule.data as ThresholdLeft | ThresholdRight).attributeComponents
      : (rule.data as DataRoute).attribute.multiLevelAttributeFilter.filterComponents;

    if (!attribute) return null;
    let descString = attribute.description;
    if (descString.endsWith('(inclusive)')) {
      descString = descString.replace('(inclusive)', '');
    } else if (descString.endsWith('(Inclusive)')) {
      descString = descString.replace('(Inclusive)', '');
    }

    if (isCustomListAttribute(attribute)) {
      const updatedDetails = updateCustomListAttribute({
        components,
        customLists,
        actionOptions: actions[ruleActionId]?.options,
        description: descString,
      });
      components = updatedDetails.components;
      descString = updatedDetails.description;
    }

    const multiLevelAttributesWithCustomSorting = [
      'th_multilevel',
      'th_nonmonetary_multilevel',
    ].includes(attribute.attribute_name);
    // for multiLevelAttributesWithCustomSorting attributes, we generate tokens using filter components, not description string
    const descTokens = multiLevelAttributesWithCustomSorting
      ? getSortedMLAComponentsByPrecedence({ mlaData: components })
      : getMLADescriptionTokens(descString);

    let presentationalTokens: PresentationalToken[] = [];

    presentationalTokens = generateCustomFootnoteTokens({
      attribute,
      components,
      descTokens,
    });

    return {
      ...rule,
      attributeName: attribute.attribute_name,
      presentationalTokens,
    };
  }).filter(Boolean);
}


export function isThresholdAnMLA(threshold: Threshold): boolean {
  const leftAttributeComponentsLength = threshold.left.attributeComponents.length;
  const rightAttributeComponentsLength = threshold.right.attributeComponents.length;

  return leftAttributeComponentsLength > 0 || rightAttributeComponentsLength > 0;
}

export function convertMLAToString({
  actions,
  services,
  sourceAction,
  mlaDataBundle,
  customLists,
}: {
  actions: Record<string, Action>;
  services: ServicesByName;
  customLists: CustomList[];
  sourceAction: Action;
  /**
   * An MLA can be present in a Threshold or in a DataRoute.
   * This bundle type ensures that only the relevant args are
   * passed depending on the source type.
   */
  mlaDataBundle:
  {
    source: 'threshold';
    thresholds: Record<string, Threshold>;
    threshold: Threshold;
  } | {
    source: 'dataroute';
    dataRoutes: Record<string, DataRoute>;
    dataRoute: DataRoute;
  };
}): string {
  let rulesWithMLA: RulesWithMLA;
  let sourceId: string;

  if (mlaDataBundle.source === 'threshold') {
    const { threshold, thresholds } = mlaDataBundle;

    if (!isThresholdAnMLA(threshold)) {
      return '';
    }

    sourceId = threshold.publicId;
    rulesWithMLA = getMLAsUsedInRules({
      sourceAction,
      thresholds,
    });
  } else {
    const { dataRoute, dataRoutes } = mlaDataBundle;

    if (!isDataRouteAnMLA(dataRoute)) {
      return '';
    }

    sourceId = dataRoute.publicId;
    rulesWithMLA = getMLAsUsedInExpression({
      dataRoutes,
      sourceAction,
    });
  }

  const allMLAsWithFootnoteString = getAllMLAsWithFootnoteStringTokens({
    actions,
    rulesWithMLA,
    services,
    customLists,
  });

  const mlaWithFootnoteString = allMLAsWithFootnoteString
    .find(item => item.publicId === sourceId);

  const mlaAsStringChunks = mlaWithFootnoteString.presentationalTokens.map(token => {
    const labelRequirements = getLabelRequirementsForFootnoteSentence({
      attributeName: mlaWithFootnoteString.attributeName,
      token,
    });


    if ((token.isOptional && !token.value)) {
      return '';
    }

    return token.component
      ? getPresentationalValue({
        attributeName: mlaWithFootnoteString.attributeName,
        component: token.component,
        configuredValue: token.value,
        labelRequirements,
      }) || `{${getMLAFilterComponentLabel({
        componentData: token.component,
      })}}`
      : token.label;
  });


  return mlaAsStringChunks.filter(Boolean).join(' ');
}


export function getMLAsUsedInRules({
  sourceAction,
  thresholds,
}: {
  sourceAction: Action;
  thresholds: Record<string, Threshold>;
}): RulesWithMLA {
  if (!sourceAction) return null;
  const allThresholdsInRules = getAllThresholdFromRules({
    rules: sourceAction.rules,
    thresholds,
  });
  const thresholdsWithMLA: RulesWithMLA = [];
  allThresholdsInRules.forEach(threshold => {
    ['left', 'right'].forEach(direction => {
      if (threshold[direction]?.attributeComponents?.length > 0) {
        thresholdsWithMLA.push({
          type: direction === 'left' ? MLAType.THRESHOLD_LEFT : MLAType.THRESHOLD_RIGHT,
          publicId: threshold.publicId,
          data: threshold[direction],
        });
      }
    });
  });
  return thresholdsWithMLA;
}


export function getMLAsUsedInExpression({
  sourceAction,
  dataRoutes,
}: {
  sourceAction: Action;
  dataRoutes: Record<string, DataRoute>;
}): RulesWithMLA {
  if (!sourceAction) return [];
  const allDataRoutesInRules = extractDataRouteIds(sourceAction.output.expression)
    .map(id => dataRoutes[id]);
  const dataRoutesWithMLA: RulesWithMLA = [];
  allDataRoutesInRules.forEach(dataRoute => {
    if (dataRoute.attribute.multiLevelAttributeFilter?.filterComponents.length > 0) {
      dataRoutesWithMLA.push({
        type: 'dataRoute',
        publicId: dataRoute.publicId,
        data: dataRoute,
      });
    }

    return null;
  });

  return dataRoutesWithMLA;
}

export function isRuleWithMLAAThreshold(ruleWithMLA: RulesWithMLA[number]): boolean {
  return ruleWithMLA.type === 'thresholdLeft' || ruleWithMLA.type === 'thresholdRight';
}

export function getSpecificMLANoOptionsMessage(componentInfo: AttributeComponentDefinition) {
  switch (componentInfo.placeholder) {
    case '{entry_mode}':
      return 'Provide an integer value. Commas are allowed. No whitespace is allowed.';
    case CUSTOM_LIST_SCORE_PLACEHOLDER:
      return 'Provide a decimal value between 0 and 1. 0 and 1 are considered no match or exact match';
    default:
      return null;
  }
}

// ============================================================
//     TRANSACTION HISTORY
// ============================================================
function customizeTransactionHistoryMLAFootnote({
  attribute,
  descTokens,
}: {
  attribute: Attribute;
  descTokens: AttributeComponent[] | RegExpMatchArray;
}) {
  let presentationalTokens: PresentationalToken[] = [];
  const tokenIndexes = descTokens.map(filterComponent => {
    const processedToken = generateTokenObject(filterComponent);
    const conditionallyLowercaseLabel = processedToken.label === 'The' ? processedToken.label : processedToken.label?.toLowerCase();
    return {
      ...processedToken,
      label: conditionallyLowercaseLabel || null,
    };
  });

  // for these attributes, we need this arbitrary ordering/tokens that's hard coded
  presentationalTokens = [
    {
      label: 'The',
      placeholder: 'the-0',
      isOptional: false,
      component: null,
      value: null,
    },
    tokenIndexes[0],
    {
      label: tokenIndexes[0].value === 'days_count' ? 'with' : 'of',
      placeholder: 'of-2',
      isOptional: false,
      component: null,
      value: null,
    },
    ...tokenIndexes.slice(1),
  ];

  const pendingDeletionToken = [];

  // custom phrases depending on the MLA filter level configuration for enhanced readability
  const onlyCounterpartyTokenIndex = presentationalTokens.findIndex(token => token.placeholder === '{only_counterparty}');
  const onlySourceAccountTokenIndex = presentationalTokens.findIndex(token => token.placeholder === '{only_source_account}');
  if (presentationalTokens[onlyCounterpartyTokenIndex]?.value
    && presentationalTokens[onlySourceAccountTokenIndex]?.value) {
    presentationalTokens[onlyCounterpartyTokenIndex] = {
      label: 'between this source account and the counterparty being evaluated,',
      placeholder: 'counterparty-source-account-both',
      isOptional: false,
      component: null,
      value: null,
    };

    pendingDeletionToken.push('{only_source_account}');
  }

  const flowFilterValue = presentationalTokens.find(token => token.placeholder === '{flow}')?.value;
  const statusTokenIndex = presentationalTokens.findIndex(token => token.placeholder === '{status}'); // required for editing label
  const statusFilterValue = presentationalTokens[statusTokenIndex]?.value;
  const geographyFilterValue = presentationalTokens.find(token => token.placeholder === '{is_international}')?.value;
  const transactionStatusFilter = presentationalTokens.find(token => token.placeholder === '{transaction_status}');
  const userTriggeredFilterValue = presentationalTokens.find(token => token.placeholder === '{usertriggered}')?.value;

  const transactionType = attribute.attribute_name === 'th_nonmonetary_multilevel'
    ? 'non-monetary transactions' : 'transactions';
  let updatedTransactionConditionLabel = transactionType;
  if (geographyFilterValue) {
    if (geographyFilterValue === 'all') {
      updatedTransactionConditionLabel = `${updatedTransactionConditionLabel} of any geography`;
    } else {
      updatedTransactionConditionLabel = `${geographyFilterValue} ${updatedTransactionConditionLabel}`;
    }

    pendingDeletionToken.push('{is_international}');
  }

  if (transactionStatusFilter?.value) {
    if (transactionStatusFilter.value === 'all') {
      updatedTransactionConditionLabel = `${updatedTransactionConditionLabel} with all transaction status`;
    } else {
      const presentationalValue = String(
        getPresentationalValue({
          configuredValue: transactionStatusFilter.value,
          component: transactionStatusFilter.component,
          attributeName: attribute.attribute_name,
          labelRequirements: false,
          shouldReturnValueOnly: true,
        }),
      ).toLowerCase();
      updatedTransactionConditionLabel = updatedTransactionConditionLabel.replace(
        transactionType,
        ` ${presentationalValue} ${transactionType}`,
      );
    }

    pendingDeletionToken.push('{transaction_status}');
  }

  if (userTriggeredFilterValue) {
    switch (userTriggeredFilterValue) {
      case 'all':
        updatedTransactionConditionLabel = `${updatedTransactionConditionLabel} that are either initiated or not initiated by user`;
        break;
      case 'user_initiated':
        updatedTransactionConditionLabel = `${updatedTransactionConditionLabel} initiated by user`;
        break;
      case 'not_user_initiated':
        updatedTransactionConditionLabel = `${updatedTransactionConditionLabel} that are not initiated by user`;
        break;
      default:
        break;
    }

    pendingDeletionToken.push('{usertriggered}');
  }

  if (flowFilterValue) {
    if (flowFilterValue === 'all') {
      updatedTransactionConditionLabel = `incoming and outgoing ${updatedTransactionConditionLabel}`;
    } else {
      updatedTransactionConditionLabel = `${flowFilterValue} ${updatedTransactionConditionLabel}`;
    }

    pendingDeletionToken.push('{flow}');
  }

  if (statusFilterValue) {
    if (statusFilterValue === 'all') {
      updatedTransactionConditionLabel = `all ${updatedTransactionConditionLabel}`;
    } else {
      updatedTransactionConditionLabel = `${statusFilterValue} ${updatedTransactionConditionLabel}`;
    }

    pendingDeletionToken.push('{status}');
  }

  presentationalTokens.splice(statusTokenIndex - 1, 0, {
    label: `${updatedTransactionConditionLabel},`,
    placeholder: 'transaction-conditions',
    isOptional: false,
    component: null,
    value: null,
  });

  const minAmountIndex = presentationalTokens.findIndex(token => token.placeholder === '{amount}');
  const maxAmountFilterValue = presentationalTokens.find(token => token.placeholder === '{max_amount}')?.value;
  const minAmountFilterValue = presentationalTokens[minAmountIndex]?.value;
  if (minAmountFilterValue && maxAmountFilterValue) {
    presentationalTokens[minAmountIndex] = {
      label: `of an amount more than $${getDollarCurrencyValue(minAmountFilterValue as string)} but less than $${getDollarCurrencyValue(maxAmountFilterValue as string)}`,
      placeholder: 'minmaxamount-both',
      isOptional: false,
      component: null,
      value: null,
    };

    pendingDeletionToken.push('{max_amount}');
  }

  // for these, we need to artificially sort these 3 items all the way to the end.
  presentationalTokens.push({
    label: 'from',
    placeholder: 'from-last',
    isOptional: false,
    component: null,
    value: null,
  });
  presentationalTokens.push(presentationalTokens.splice(presentationalTokens.findIndex(v => v.placeholder === '{date_range_start}'), 1)[0]);
  presentationalTokens.push({
    label: 'to',
    placeholder: 'to-last',
    isOptional: false,
    component: null,
    value: null,
  });
  presentationalTokens.push(presentationalTokens.splice(presentationalTokens.findIndex(v => v.placeholder === '{date_range_end}'), 1)[0]);
  presentationalTokens.push(presentationalTokens.splice(presentationalTokens.findIndex(v => v.placeholder === '{interval}'), 1)[0]);
  presentationalTokens.push({
    label: 'ago',
    placeholder: 'ago-last',
    isOptional: false,
    component: null,
    value: null,
  });

  return presentationalTokens.filter(token => !pendingDeletionToken.includes(token.placeholder));
}

function customizeTransactionHistoryMLAEditableFilters({
  attribute,
  descTokens,
}: {
  attribute: Attribute;
  descTokens: AttributeComponent[] | RegExpMatchArray;
}) {
  let tokensWithIndex: {
    string: string;
    isOptional: boolean;
    caretIndex: number;
  }[] = [];
  const tokenIndexes = descTokens.map(filterComponent => ({
    string: filterComponent.attributeComponent.placeholder,
    isOptional: filterComponent.attributeComponent.isOptional,
    caretIndex: filterComponent.attributeComponent.precedence,
  }));

  // for these attributes, we need this arbitrary ordering/tokens that's hard coded
  tokensWithIndex = [
    {
      string: 'The',
      isOptional: false,
      caretIndex: undefined,
    },
    tokenIndexes[0],
    {
      string: 'of',
      isOptional: false,
      caretIndex: undefined,
    },
    tokenIndexes[1],
    {
      string: `${attribute.attribute_name === 'th_nonmonetary_multilevel'
        ? 'Non-Monetary Transactions' : 'Transactions'} where`,
      isOptional: false,
      caretIndex: undefined,
    },
    ...tokenIndexes.slice(2),
  ];

  const statusTokenIndex = tokensWithIndex.findIndex(token => token.string === '{status}');
  if (statusTokenIndex !== -1) {
    tokensWithIndex.splice(statusTokenIndex, 0, {
      string: 'status is',
      isOptional: false,
      caretIndex: undefined,
    });
  }

  // for these, we need to artificially sort these 3 items all the way to the end with additional extra strings
  tokensWithIndex.push({
    string: 'from',
    isOptional: false,
    caretIndex: undefined,
  });
  tokensWithIndex.push(tokensWithIndex.splice(tokensWithIndex.findIndex(v => v.string === '{date_range_start}'), 1)[0]);
  tokensWithIndex.push({
    string: 'to',
    isOptional: false,
    caretIndex: undefined,
  });
  tokensWithIndex.push(tokensWithIndex.splice(tokensWithIndex.findIndex(v => v.string === '{date_range_end}'), 1)[0]);
  tokensWithIndex.push(tokensWithIndex.splice(tokensWithIndex.findIndex(v => v.string === '{interval}'), 1)[0]);
  tokensWithIndex.push({
    string: 'ago',
    isOptional: false,
    caretIndex: undefined,
  });

  return tokensWithIndex;
}

function customizeTHCounterpartyMLAFootnote({
  processedTokens,
}: {
  processedTokens: PresentationalToken[];
}) {
  let presentationalTokens: PresentationalToken[] = [];

  // for these attributes, we need this arbitrary ordering/tokens that's hard coded
  presentationalTokens = [
    ...processedTokens.slice(0, 4),
    {
      label: 'the',
      placeholder: 'the-4',
      isOptional: false,
      component: null,
      value: null,
    },
    ...processedTokens.slice(4),
  ];

  presentationalTokens[3].label = '—';

  // custom phrases depending on the MLA filter level configuration for enhanced readability
  const withLabelTokenIndex = presentationalTokens.findIndex(token => token.label === 'with');
  const minCountIndex = presentationalTokens.findIndex(token => token.placeholder === '{min_count}');
  const maxCountIndex = presentationalTokens.findIndex(token => token.placeholder === '{max_count}');
  const minCountFilterValue = presentationalTokens[minCountIndex]?.value;
  const maxCountFilterValue = presentationalTokens[maxCountIndex]?.value;
  let updatedMinMaxCountLabel = '';
  let tokenDeletionCount = 6;
  if (minCountFilterValue && maxCountFilterValue) {
    updatedMinMaxCountLabel = `between ${minCountFilterValue} and ${maxCountFilterValue}`;
    tokenDeletionCount = 7;
  } else if (minCountFilterValue) {
    updatedMinMaxCountLabel = `with more than ${minCountFilterValue}`;
  } else if (maxCountFilterValue) {
    updatedMinMaxCountLabel = `with less than ${maxCountFilterValue}`;
  } else if (!minCountFilterValue && !maxCountFilterValue) {
    updatedMinMaxCountLabel = 'for';
  }
  presentationalTokens[withLabelTokenIndex] = {
    label: updatedMinMaxCountLabel,
    placeholder: 'minmaxcount-custom',
    isOptional: false,
    component: null,
    value: null,
  };

  presentationalTokens.splice(withLabelTokenIndex + 1, tokenDeletionCount);

  const flowTokenIndex = presentationalTokens.findIndex(token => token.placeholder === '{flow}');
  const userTriggeredTokenIndex = presentationalTokens.findIndex(token => token.placeholder === '{usertriggered}');
  const flowFilterValue = presentationalTokens[flowTokenIndex]?.value;
  const userTriggeredFilterValue = presentationalTokens[userTriggeredTokenIndex]?.value;
  let updatedTransactionDetailsLabel = '';
  if (flowFilterValue === 'all') {
    updatedTransactionDetailsLabel = 'incoming and outgoing transactions';
  } else {
    updatedTransactionDetailsLabel = `${(flowFilterValue as string).toLowerCase()} transactions`;
  }

  if (userTriggeredFilterValue) {
    switch (userTriggeredFilterValue) {
      case 'all':
        updatedTransactionDetailsLabel = `${updatedTransactionDetailsLabel} that are either initiated or not initiated by user`;
        break;
      case 'user_initiated':
        updatedTransactionDetailsLabel = `user-initiated ${updatedTransactionDetailsLabel}`;
        break;
      case 'not_user_initiated':
        updatedTransactionDetailsLabel = `${updatedTransactionDetailsLabel} that are not initiated by user`;
        break;
      default:
        break;
    }

    presentationalTokens.splice(userTriggeredTokenIndex, 1);
  }

  presentationalTokens[flowTokenIndex] = {
    label: updatedTransactionDetailsLabel,
    placeholder: 'flow-userinit-trx-custom',
    isOptional: false,
    component: null,
    value: null,
  };

  return presentationalTokens;
}

function customizeALHNumberJourneysOrALHNumberInvestigationsMLAFootnote({
  processedTokens,
}: {
  processedTokens: PresentationalToken[];
}) {
  const presentationalTokens: PresentationalToken[] = [...processedTokens];

  const outcomeTokenIdx = presentationalTokens.findIndex(token => token.placeholder === '{outcome}');
  const tagTokenIdx = presentationalTokens.findIndex(token => token.placeholder === '{tag}');
  const outcomeComponent = { ...presentationalTokens[outcomeTokenIdx].component };
  const tagComponent = { ...presentationalTokens[tagTokenIdx].component };

  if (presentationalTokens[outcomeTokenIdx].value) {
    // Update the outcome component label
    outcomeComponent.label = 'with outcome';
    presentationalTokens[outcomeTokenIdx].component = outcomeComponent;

    // Update the tag component label
    if (presentationalTokens[tagTokenIdx].value) {
      tagComponent.label = 'and tag';
      presentationalTokens[tagTokenIdx].component = tagComponent;
    }
  } else if (presentationalTokens[tagTokenIdx].value) {
    // Update the tag component label
    tagComponent.label = 'with tag';
    presentationalTokens[tagTokenIdx].component = tagComponent;
  }

  return presentationalTokens;
}
